import { useEffect, useState } from 'react';
import {
  createShortLink,
  deleteShortLink,
  editAgent,
  getAgent,
  getShortLinkById,
  updateShortLink,
} from '../../../api';
import type { CreateURLPayload } from '../../../api';
import { notify } from '../../../app';
import { useModal, useRequest } from '../../../hooks';
import type { ShortLinkFormProps } from '../WebLinkGeneratorTab/components/ShortLinkForm';
import { useRouteMatch } from 'react-router';

export const SHORT_LINK_BASE = process.env.REACT_APP_SHORT_LINK_BASE_URL || '';

const defaultShortLinkForm: ShortLinkFormProps = {
  short_link_identifier: '',
};

export const useShortLink = () => {
  const { params } = useRouteMatch<{
    agent_id: string;
  }>();

  const { isOpened, onOpen, onClose } = useModal();
  const [formId, setFormId] = useState(1);
  const [shortLinkForm, setShortLinkForm] =
    useState<ShortLinkFormProps>(defaultShortLinkForm);
  const [originalUrl, setOriginalUrl] = useState<string | null>(null);
  const [isMatched, setIsMatched] = useState(true);
  const [agentData, setAgentData] = useState<any>(null);

  const [handleGetShortLinkById, handleGettingShortLinkById] = useRequest(
    (_signal, id: string) => {
      return getShortLinkById(id);
    },
    {
      deps: [],
      onSuccess: data => {
        setShortLinkForm({ short_link_identifier: data.short_id });
        setOriginalUrl(data.original_url);
      },
    }
  );

  const [handleGetAgent, loadingAgent] = useRequest(
    () => getAgent(params?.agent_id),
    {
      deps: [params?.agent_id],

      onSuccess: newData => {
        if (newData) {
          setAgentData(newData);
        }
      },
    }
  );

  const [handleUpdateAgent, editing] = useRequest(
    async (_signal, short_link: string | null) => {
      const latestAgentData = await handleGetAgent();
      if (!latestAgentData || !latestAgentData.id) {
        return;
      }

      const updatedAgentData: any = {
        ...latestAgentData,
        pipeline_settings: {
          ...latestAgentData.pipeline_settings,
          short_web_link: short_link,
        },
      };

      return editAgent(latestAgentData.id, updatedAgentData);
    },
    {
      deps: [agentData?.id],
      onSuccess: newData => {
        setAgentData(newData);
      },
      onError: () => notify.error('Failed to update agent'),
    }
  );

  const [handleCreateShortLink, handleCreatingShortLink] = useRequest(
    (_signal, payload: CreateURLPayload) => {
      return createShortLink(payload);
    },
    {
      deps: [],
      onSuccess: data => {
        if (!data.error_type) {
          handleUpdateAgent(data.short_id as string);
          onClose?.();
        }
        data.success
          ? notify.success('Short link created successfully')
          : notify.warn(data.message);
      },
      onError: () => {
        notify.error('Failed to create short link');
      },
    }
  );

  const [handleDeleteShortLink, handleDeletingShortLink] = useRequest(
    (_signal, id: string) => {
      return deleteShortLink(id);
    },
    {
      deps: [],
      onSuccess: () => {
        handleUpdateAgent(null);
        setOriginalUrl(null);
        setShortLinkForm(defaultShortLinkForm);
        setIsMatched(true);
        notify.success('Short link deleted successfully');
      },
      onError: () => {
        notify.error('Failed to delete short link');
      },
    }
  );

  const [handleUpdatehortLink, handleUpdatingShortLink] = useRequest(
    (_signal, id: string, payload: any) => {
      return updateShortLink(id, payload);
    },
    {
      deps: [],
      onSuccess: () => {
        onClose?.();
        setIsMatched(true);
        notify.success('Short link updated successfully');
      },
      onError: () => {
        notify.error('Failed to update short link');
      },
    }
  );

  useEffect(() => {
    handleGetAgent();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const shortWebLink = agentData?.pipeline_settings?.short_web_link;
    if (shortWebLink) {
      handleGetShortLinkById(shortWebLink);
    } else {
      setOriginalUrl(null);
      setShortLinkForm(defaultShortLinkForm);
    }
  }, [agentData?.pipeline_settings?.short_web_link, handleGetShortLinkById]);

  return {
    handleGetShortLinkById,
    handleGettingShortLinkById,
    handleCreateShortLink,
    handleCreatingShortLink,
    handleDeleteShortLink,
    handleDeletingShortLink,
    isOpened,
    onOpen,
    onClose,
    shortLinkForm,
    setShortLinkForm,
    formId,
    handleUpdateAgent,
    isEditingAgent: editing,
    originalUrl,
    isMatched,
    setIsMatched,
    handleUpdatehortLink,
    agentData,
    handleUpdatingShortLink,
  };
};
